import { State } from 'easy-peasy';
import { v4 as UUID } from 'uuid';
import { CocoaAPI } from '../typings';
import { ConfigState } from '../store/config';
import { PlaybackError } from '../errors/PlaybackError';
import { ErrorCodes } from '../errors/ErrorCodes';

interface GetPlaybackDataOptions {
  config: State<ConfigState>;
}

export const VideoService = {
  async getPlaybackData({ config }: GetPlaybackDataOptions): Promise<CocoaAPI.Playback> {
    let calls = 0;
    let mediaData: CocoaAPI.Playback | null = null;
    let error = null;

    this.abortController = new AbortController();

    while (calls <= config.videoApiRetryAttempts && mediaData === null) {
      calls += 1;
      error = null;

      console.log(`Fetching data from Cocoa API. Attempt ${calls}`);

      const url = `${config.cocoaApiUrl}/playback/${config.videoGuid}`;

      const headers: Record<string, string> = {
        platform: 'webvideoplayer',
        'platform-version': config.version,
        'x-api-key': config.cocoaApiKey!,
        'x-transaction-id': UUID(),
        'accept-language': config.language!,
      };

      // Send auth headers when getAccessToken is available
      if (config.getAccessToken) {
        try {
          const forceRefresh = calls > 1;
          const { accessToken, tokenType } = await config.getAccessToken({ forceRefresh });

          if (accessToken && tokenType) {
            headers['Authorization'] = `${tokenType} ${accessToken}`;
          }
        } catch (error) {
          console.error(`[Video Player] ${error}`);
        }
      }

      if (config.useBodwebHeader) headers['bodweb'] = 'true';
      if (config.simulateVideoApiAuthorizer) headers['x-username'] = config.userGuid!;

      try {
        const response = await fetch(url, {
          headers,
          signal: this.abortController.signal,
        });

        const result = await response.json();
        if (!response.ok) {
          error = new PlaybackError(response.status, result.message);
        } else {
          mediaData = result as CocoaAPI.Playback;
        }
      } catch (e) {
        error = new PlaybackError(ErrorCodes.DEFAULT, (e as Error).message);
      }

      if (error) await new Promise(resolve => setTimeout(resolve, 500));
    }

    if (error) {
      throw error;
    }
    return mediaData as CocoaAPI.Playback;
  },

  abortController: new AbortController(),
};
